
import { Button } from "@/components/ui/button";
import { CalendarClock, Video, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useA2AProtocol } from "@/hooks/useA2AProtocol";
import type { Candidate } from "../types";
import { logErrorToast } from "@/utils/toastLogger";
import { getErrorMessage } from "@/utils/errorHandling";
import { useCandidates } from "@/hooks/useCandidates";

interface StatusActionsProps {
  status?: string | null;
  candidate: Candidate;
  onMarkAsHiredRequest?: (candidate: Candidate) => void;
}

export const StatusActions = ({ status, candidate, onMarkAsHiredRequest }: StatusActionsProps) => {
  const { runMultiAgentTask } = useA2AProtocol();
  const { updateCandidateStatus, isUpdatingStatus } = useCandidates();

  const handleScreeningAction = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const task = `Screen candidate ${candidate.first_name} ${candidate.last_name} for initial qualification`;
      await runMultiAgentTask(task, ['screening-agent']);
      toast.success('Screening process initiated');
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      console.error('Error initiating screening:', error);
      logErrorToast(`Failed to initiate screening process: ${errorMsg}`, error);
    }
  };

  const handleScheduleAction = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const task = `Schedule an interview with candidate ${candidate.first_name} ${candidate.last_name}`;
      await runMultiAgentTask(task, ['scheduling-agent']);
      toast.success('Interview scheduling initiated');
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      console.error('Error scheduling interview:', error);
      logErrorToast(`Failed to schedule interview: ${errorMsg}`, error);
    }
  };

  const handleOfferAction = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!candidate.id || isUpdatingStatus) return;
    const oldStatus = candidate.status || undefined;
    updateCandidateStatus(
      { candidateId: candidate.id, status: 'offer', oldStatus },
      {
        onSuccess: () => {
          toast.success(`${candidate.first_name || 'Candidate'} moved to Offer stage.`);
        },
        onError: (error) => {
          const errorMsg = getErrorMessage(error);
          logErrorToast(`Failed to move candidate to Offer: ${errorMsg}`, error);
        },
      }
    );
  };

  const handleMarkAsHiredAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Use the callback to request the hiring form from the parent
    if (onMarkAsHiredRequest) {
      onMarkAsHiredRequest(candidate);
    }
  };

  switch (status) {
    case 'new':
      return (
        <Button
          size="sm"
          variant="outline"
          className="w-full mt-2 gap-2"
          onClick={handleScreeningAction}
          aria-label={`Screen ${candidate.first_name} ${candidate.last_name}`}
          disabled={isUpdatingStatus}
        >
          <Video className="h-4 w-4" />
          Screen Candidate
        </Button>
      );
    case 'contacted':
      return (
        <Button
          size="sm"
          variant="outline"
          className="w-full mt-2 gap-2"
          onClick={handleScheduleAction}
          aria-label={`Schedule interview with ${candidate.first_name} ${candidate.last_name}`}
          disabled={isUpdatingStatus}
        >
          <CalendarClock className="h-4 w-4" />
          Schedule Interview
        </Button>
      );
    case 'interview':
      return (
        <Button
          size="sm"
          variant="outline"
          className="w-full mt-2 gap-2"
          onClick={handleOfferAction}
          aria-label={`Move ${candidate.first_name} ${candidate.last_name} to offer`}
          disabled={isUpdatingStatus}
        >
          <ArrowRight className="h-4 w-4" />
          {isUpdatingStatus ? 'Moving...' : 'Move to Offer'}
        </Button>
      );
    case 'offer':
      return (
        <Button
          size="sm"
          variant="outline"
          className="w-full mt-2 gap-2"
          onClick={handleMarkAsHiredAction}
          aria-label={`Mark ${candidate.first_name} ${candidate.last_name} as hired`}
          disabled={isUpdatingStatus}
        >
          <Check className="h-4 w-4" />
          Mark as Hired
        </Button>
      );
    default:
      return null;
  }
};
